import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Loader } from '@hivebrite/design-system/Loader';
import { useScrollTop } from '@hivebrite/react-hooks';

import SuccessConfetti from 'hb-react/frontoffice/components/SuccessConfetti';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import SuccessMessage from 'hb-react/shared/apps/Events/pages/Booking/pages/Success/SuccessMessage';
import SuccessWithUpdateUserModal from 'hb-react/shared/apps/Events/pages/Booking/pages/Success/SuccessWithUpdateUserModal';
import type { Event } from 'hb-react/shared/apps/Events/types';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import type {
  Cart,
  CartItem,
  CartItemType,
} from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { useTrackEventOnce } from 'hb-react/shared/hooks/useAmplitude';

const sumCartItems = (cartItems: CartItem[], type: CartItemType) =>
  cartItems
    .filter((item) => item.line_itemable_type === type)
    .reduce((acc, item) => acc + item.total, 0);

const SuccessContent = ({ event, cart }: { event: Event; cart: Cart }) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();

  const displayConfetti =
    currentNetwork?.network_configuration?.events
      ?.confetti_on_reg_success_enabled;

  useScrollTop();

  useTrackEventOnce('NetworkEvents|User completes registration', {
    cart_total: cart.total,
    addons_total: sumCartItems(
      cart.cart_items,
      'Ecommerce::Products::EventAddon',
    ),
    donations_total: sumCartItems(cart.cart_items, 'Donation::Campaign'),
    tickets_total: sumCartItems(cart.cart_items, 'NetworkEvents::Ticket'),
    cart_currency: cart.currency.code,
    number_of_items: cart.cart_items.length,
  });

  const topicId = topicContext?.topicId;
  const isWhitelabelGroup = !!topicContext?.whitelabelURL;

  return (
    <>
      <SuccessConfetti enabled={!!displayConfetti} />

      <Box flexDirection="column">
        <SuccessMessage
          selectedMediaIcons={event?.social_media_list}
          headline={event?.success_page?.headline}
          tagline={event?.success_page?.tagline}
          successPageContent={event?.success_page?.success_page}
          id={event.id}
          timezone={event.timezone}
          eventType={event.type}
          sharingParameters={{
            title: event.title,
            url: `${window.location.origin}${Routes.networks_event_path(
              event.id,
            )}`,
          }}
        />
        <Box pt={5}>
          {currentUser && (
            <Box mr={5}>
              <ButtonInLink
                linkProps={{
                  to: Routes.me_my_events_path(),
                }}
              >
                {__('See your events')}
              </ButtonInLink>
            </Box>
          )}

          <Button
            onClick={() => {
              const newLocation = getEventDedicatedPath({
                hasUser: !!currentUser,
                event,
                topicId,
                isWhitelabelGroup,
              });

              window.location.href = newLocation;
            }}
            variant="secondary"
          >
            {__('See event detail page')}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export const SuccessPage = () => {
  const { event, cart } = useBooking();

  if (!event || !cart) {
    return <Loader />;
  }

  return (
    <SuccessWithUpdateUserModal>
      <SuccessContent event={event} cart={cart} />
    </SuccessWithUpdateUserModal>
  );
};
